/**
 * On/off switch of the "Exclusive offers" card: on adds the add-on product to the cart, off removes it again.
 * The switch follows the real cart, so it is on when the product is already in the cart (also after a reload).
 * The cart count in the header is updated without leaving the page.
 *
 * Offer link: when the 3 + 2 bundle is selected the kit is included, so the switch is on automatically (and locked),
 * the price reads FREE with the before price still struck through. With "1 nail pen" selected the switch is off
 * unless the kit is in the cart, and the normal price is shown. In the bundle state the cart is not touched.
 */

const SWITCH = '.addon-offer__switch';

async function readCart() {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  return response.json();
}

/** @param {{ item_count: number }} cart */
function updateCartIcon(cart) {
  const icon = document.querySelector('cart-icon');
  if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count);
}

/** @param {HTMLElement} el @param {boolean} on */
function setState(el, on) {
  el.setAttribute('aria-checked', String(on));
}

const OFFER = '.product-details .offer-card';

function bundleSelected() {
  const checked = document.querySelector(`${OFFER} input[type="radio"]:checked`);
  return checked instanceof HTMLInputElement && checked.value !== '1';
}

/** Shows the kit as included (FREE, switch on and locked) in the bundle, and as a normal add-on otherwise. */
function applyOfferMode() {
  const included = bundleSelected();

  document.querySelectorAll('.addon-offer').forEach((card) => {
    const switchEl = card.querySelector(SWITCH);
    const price = card.querySelector('.addon-offer__price');
    const badge = card.querySelector('.addon-offer__badge');

    if (price instanceof HTMLElement) {
      price.dataset.original ??= price.textContent ?? '';
      price.textContent = included ? 'FREE' : price.dataset.original;
    }
    if (badge instanceof HTMLElement) {
      badge.dataset.original ??= badge.textContent ?? '';
      badge.textContent = included ? 'Bundle gift' : badge.dataset.original;
    }

    if (switchEl instanceof HTMLButtonElement) {
      switchEl.dataset.included = String(included);
      switchEl.disabled = included;
      if (included) setState(switchEl, true);
    }
  });

  if (!included) syncSwitches();
}

/** Sets every switch to the state of the cart. */
async function syncSwitches() {
  const switches = document.querySelectorAll(SWITCH);
  if (!switches.length) return;

  try {
    const cart = await readCart();
    switches.forEach((el) => {
      if (!(el instanceof HTMLElement) || el.dataset.busy === 'true' || el.dataset.included === 'true') return;
      const id = Number(el.dataset.variantId);
      setState(el, cart.items.some((item) => item.variant_id === id));
    });
  } catch {
    // Leave the switches as they are when the cart cannot be read.
  }
}

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const el = target?.closest(SWITCH);
  if (!(el instanceof HTMLButtonElement) || el.dataset.busy === 'true' || el.dataset.included === 'true') return;

  const id = Number(el.dataset.variantId);
  if (!id) return;

  const turnOn = el.getAttribute('aria-checked') !== 'true';
  el.dataset.busy = 'true';
  el.disabled = true;
  setState(el, turnOn); // show the new position straight away

  try {
    const response = turnOn
      ? await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
        })
      : await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: String(id), quantity: 0 }),
        });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    updateCartIcon(await readCart());
  } catch {
    setState(el, !turnOn); // it did not work (for example sold out): put the switch back
  } finally {
    el.dataset.busy = 'false';
    el.disabled = el.dataset.included === 'true';
  }
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyOfferMode);
else applyOfferMode();

window.addEventListener('pageshow', applyOfferMode);

// Choosing the bundle or "1 nail pen", or the page content being swapped by the colour switcher.
document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.type === 'radio' && event.target.closest(OFFER)) {
    applyOfferMode();
  }
});
document.addEventListener('shade-switcher:updated', applyOfferMode);
// The page content can be swapped without a reload (colour switcher): sync the new switch too.
let pending = false;
new MutationObserver(() => {
  if (pending) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    if (document.querySelector(`${SWITCH}:not([data-synced])`)) {
      document.querySelectorAll(SWITCH).forEach((el) => el instanceof HTMLElement && (el.dataset.synced = 'true'));
      applyOfferMode();
    }
  });
}).observe(document.documentElement, { childList: true, subtree: true });
